import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, CheckCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EmailCaptureProps {
  source: string;
}

export const EmailCapture = ({ source }: EmailCaptureProps) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from("email_subscribers" as any)
        .insert({ email: trimmed, source } as any);

      if (error) {
        if (error.code === "23505") {
          // Duplicate — still show success
          setSubmitted(true);
        } else {
          throw error;
        }
      } else {
        setSubmitted(true);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-8 text-center">
          <CheckCircle className="h-10 w-10 text-primary mx-auto mb-3" />
          <p className="text-lg font-semibold text-foreground">You're on the list!</p>
          <p className="text-sm text-muted-foreground mt-1">
            We'll notify you before peak travel days.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-primary/20 bg-gradient-to-r from-primary/5 to-accent/5">
      <CardContent className="py-8">
        <div className="max-w-md mx-auto text-center space-y-4">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10">
            <Bell className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground">
            Get TSA Wait Time Alerts
          </h3>
          <p className="text-sm text-muted-foreground">
            We'll notify you before peak travel days so you never get stuck in line.
          </p>
          <form onSubmit={handleSubmit} className="flex gap-2 max-w-sm mx-auto">
            <Input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              maxLength={255}
              className="flex-1 min-h-[44px]"
              aria-label="Email address"
            />
            <Button type="submit" disabled={loading} className="min-h-[44px] px-6">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Notify Me"}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">No spam. Unsubscribe anytime.</p>
        </div>
      </CardContent>
    </Card>
  );
};
